<script setup lang="ts">
/**
 * 确认框宿主。挂在 App.vue 顶层，全局只有一个。
 *
 * 危险操作（停止、删除、吊销）的确认按钮用 destructive 色 —— §9.11.8 的
 * 代价条款③：多一次点击换「不会有人手滑停掉别人的训练」。
 */
import { computed, ref, watch } from 'vue'

import AppButton from './AppButton.vue'
import AppCheckbox from './AppCheckbox.vue'
import AppDialog from './AppDialog.vue'
import { useConfirmState } from '@/composables/useConfirm'

const { pending, accept, dismiss } = useConfirmState()

const open = computed({
  get: () => pending.value !== null,
  // 只有关闭（Esc / 点遮罩）会走到这里，等同于取消。
  set: (v: boolean) => {
    if (!v) dismiss()
  },
})

/**
 * 勾选框的状态。每次开框都**重置成不勾**。
 *
 * 宿主是全局单例，状态会跨对话框留存 —— 上一次删除时勾了「连目录一起删」，
 * 下一次删另一台机器的副本时它还亮着，而用户以为自己在做上次那个动作。
 */
const checked = ref(false)
watch(pending, (p) => {
  if (p) checked.value = false
})

const danger = computed(() => pending.value?.type === 'warning' || pending.value?.type === 'error')
</script>

<template>
  <!-- `layer="top"`：确认框会从别的对话框里被弹出来（发布面板的重置/关闭、
       提示词库的删除）。同档时后挂载的在上，而本宿主是全局单例、挂载最早 ——
       不抬档就会被那个对话框整片盖住，确认按钮点不到。 -->
  <AppDialog v-model="open" :title="pending?.title ?? ''" width="26rem" layer="top">
    <!-- `whitespace-pre-wrap`：危险操作的影响面是**分行**写的
         （「删了之后还能做什么 / 失去什么」各列几条）。压成一整段的结果是
         用户一个字都不看，直接点确定 —— 而那正是这个对话框想拦住的事。 -->
    <p class="whitespace-pre-wrap text-sm leading-relaxed text-foreground">{{ pending?.message }}</p>

    <!-- 附加勾选项：同一个动作，勾上之后后果更重。默认不勾（见 useConfirm.ts）。 -->
    <div v-if="pending?.checkbox" class="mt-4 rounded-md border border-border bg-muted/40 p-3">
      <AppCheckbox v-model="checked" data-test="confirm-checkbox">
        {{ pending.checkbox.label }}
      </AppCheckbox>
      <p v-if="pending.checkbox.hint" class="mt-1.5 pl-6 text-xs leading-relaxed text-muted-foreground">
        {{ pending.checkbox.hint }}
      </p>
    </div>

    <template #footer>
      <AppButton variant="outline" data-test="confirm-cancel" @click="dismiss">
        {{ pending?.cancelButtonText ?? '取消' }}
      </AppButton>
      <!--
        `data-test` 而不是靠按钮文案定位：确认文案是每个调用点自己给的
        （「删除」「撤销」「停止」…），而页面上常常同时存在一个同名的触发按钮 ——
        `getByRole('button', {name:'撤销'})` 会同时命中「撤销全部编辑」和这一个。
      -->
      <AppButton
        :variant="danger ? 'destructive' : 'default'"
        data-test="confirm-ok"
        @click="accept(checked)"
      >
        {{ pending?.confirmButtonText ?? '确定' }}
      </AppButton>
    </template>
  </AppDialog>
</template>
